import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Checkbox,
  ConfigProvider,
  DatePicker,
  Empty,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Segmented,
  Select,
  Spin,
  Switch,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { toastr } from 'react-redux-toastr';
import {
  abortScrape,
  fetchScrapeThumb,
  getApifyStatus,
  getScrape,
  importScrapedPosts,
  listScrapes,
  removeScrape,
  startScrape,
} from 'state/actions/apify';
import './HashtagScraper.css';

const PLATFORM_META = {
  instagram: { label: 'Instagram', icon: 'feather-instagram' },
  tiktok: { label: 'TikTok', icon: 'feather-music' },
  x: { label: 'X', icon: 'feather-twitter' },
};
const RUNNING = ['READY', 'RUNNING', 'TIMING-OUT', 'ABORTING'];
const POLL_MS = 4000;
const HASHTAG_RE = /^[\p{L}\p{N}_]{1,100}$/u;

const isRunning = (job) => job && RUNNING.includes(job.status);
const platformLabel = (p) => PLATFORM_META[p]?.label || p;
const fmtDate = (d) => (d ? dayjs(d).format('DD MMM YYYY, HH:mm') : '-');
const fmtNum = (n) =>
  n === undefined || n === null
    ? null
    : n >= 1e6
    ? `${(n / 1e6).toFixed(1)}M`
    : n >= 1e3
    ? `${(n / 1e3).toFixed(1)}K`
    : String(n);
const cleanTag = (t) =>
  String(t || '')
    .trim()
    .replace(/^#+/, '')
    .toLowerCase();

const STATUS_PILL = {
  READY: { label: 'Queued', tone: 'info' },
  RUNNING: { label: 'Scraping', tone: 'info' },
  'TIMING-OUT': { label: 'Timing out', tone: 'warning' },
  ABORTING: { label: 'Cancelling', tone: 'warning' },
  SUCCEEDED: { label: 'Done', tone: 'success' },
  FAILED: { label: 'Failed', tone: 'danger' },
  'TIMED-OUT': { label: 'Timed out', tone: 'warning' },
  ABORTED: { label: 'Cancelled', tone: 'muted' },
};

const theme = {
  token: {
    colorPrimary: '#64D5D2',
    colorLink: '#2C82F6',
    fontFamily: "'Inter', sans-serif",
    borderRadius: 6,
  },
};

/* ------------------------------ thumbnails ------------------------------ */

// Thumbnails need the auth header, so they're fetched as blobs. A tiny queue
// keeps it to a few parallel requests even with 100 cards on screen.
const thumbCache = new Map(); // key -> objectURL | 'error'
const thumbQueue = [];
let thumbActive = 0;
const THUMB_PARALLEL = 4;

const pumpThumbs = () => {
  while (thumbActive < THUMB_PARALLEL && thumbQueue.length) {
    const task = thumbQueue.shift();
    thumbActive += 1;
    task().finally(() => {
      thumbActive -= 1;
      pumpThumbs();
    });
  }
};

const loadThumb = (params) => {
  const key = `${params.id}:${params.postId}`;
  if (thumbCache.has(key)) return Promise.resolve(thumbCache.get(key));
  return new Promise((resolve) => {
    thumbQueue.push(() =>
      fetchScrapeThumb(params)
        .then((blob) => {
          const objectUrl = URL.createObjectURL(blob);
          thumbCache.set(key, objectUrl);
          resolve(objectUrl);
        })
        .catch(() => {
          thumbCache.set(key, 'error');
          resolve('error');
        })
    );
    pumpThumbs();
  });
};

export const clearThumbCache = () => {
  thumbQueue.length = 0;
  thumbCache.forEach((v) => v !== 'error' && URL.revokeObjectURL(v));
  thumbCache.clear();
};

function Thumb({ siteId, jobId, postId, alt }) {
  const ref = useRef(null);
  const key = `${jobId}:${postId}`;
  const [src, setSrc] = useState(thumbCache.get(key) || null);

  useEffect(() => {
    if (src) return undefined;
    let alive = true;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        loadThumb({ siteId, id: jobId, postId }).then(
          (v) => alive && setSrc(v)
        );
      },
      { rootMargin: '200px' }
    );
    if (el) io.observe(el);
    return () => {
      alive = false;
      io.disconnect();
    };
  }, [siteId, jobId, postId, src]);

  return (
    <div ref={ref} className="hs-thumb">
      {!src && <div className="hs-thumb__skeleton" />}
      {src === 'error' && (
        <div className="hs-thumb__error">
          <i className="feather-image" />
          <span>Preview unavailable</span>
        </div>
      )}
      {src && src !== 'error' && <img src={src} alt={alt} draggable={false} />}
    </div>
  );
}

/* ------------------------------ hashtag input ---------------------------- */

function HashtagInput({ value, onChange, onSubmit, placeholder }) {
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  const commit = (raw) => {
    const parts = String(raw)
      .split(/[\s,]+/)
      .map(cleanTag)
      .filter(Boolean);
    if (parts.length) onChange([...value, ...parts]);
    setDraft('');
  };

  return (
    <div
      className="hs-tags"
      onClick={() => inputRef.current && inputRef.current.focus()}
    >
      <i className="feather-hash hs-muted" />
      {value.map((t) => (
        <span key={t} className="hs-chip">
          #{t}
          <i
            className="feather-x"
            role="button"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              onChange(value.filter((v) => v !== t));
            }}
          />
        </span>
      ))}
      <input
        ref={inputRef}
        value={draft}
        placeholder={value.length ? '' : placeholder}
        onChange={(e) => {
          const v = e.target.value;
          if (/[\s,]$/.test(v)) commit(v);
          else setDraft(v);
        }}
        onPaste={(e) => {
          e.preventDefault();
          commit(`${draft} ${e.clipboardData.getData('text')}`);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (draft.trim()) commit(draft);
            else onSubmit();
          } else if (e.key === 'Backspace' && !draft && value.length) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={() => draft.trim() && commit(draft)}
      />
    </div>
  );
}

/* -------------------------------- post card ------------------------------ */

function PostCard({ siteId, jobId, post, selected, onToggle }) {
  const disabled = post.imported;
  return (
    <div
      role="button"
      tabIndex={0}
      className={`hs-card${selected ? ' is-selected' : ''}${
        disabled ? ' is-imported' : ''
      }`}
      onClick={() => !disabled && onToggle(post.postId)}
      onKeyDown={(e) => {
        if (!disabled && (e.key === ' ' || e.key === 'Enter')) {
          e.preventDefault();
          onToggle(post.postId);
        }
      }}
    >
      <div className="hs-card__media">
        <Thumb
          siteId={siteId}
          jobId={jobId}
          postId={post.postId}
          alt={post.caption?.slice(0, 60) || ''}
        />
        {post.isVideo && (
          <span className="hs-badge hs-badge--type">
            <i className="feather-play" />
            {post.videoDuration
              ? `${Math.round(post.videoDuration)}s`
              : 'Video'}
          </span>
        )}
        {!post.isVideo && post.isCarousel && (
          <span className="hs-badge hs-badge--type">
            <i className="feather-layers" />
            Album
          </span>
        )}
        {disabled ? (
          <span className="hs-card__imported">
            <i className="feather-check" /> In Media
          </span>
        ) : (
          <span className={`hs-card__check${selected ? ' is-on' : ''}`}>
            {selected && <i className="feather-check" />}
          </span>
        )}
      </div>
      <div className="hs-card__body">
        <div className="hs-card__meta">
          <span
            className="hs-card__author"
            title={post.authorName || post.author}
          >
            @{post.author || 'unknown'}
          </span>
          <span className="hs-muted">
            {post.postedAt ? dayjs(post.postedAt).format('DD MMM') : ''}
          </span>
        </div>
        <p className="hs-card__caption">
          {post.caption || <i className="hs-muted">No caption</i>}
        </p>
        <div className="hs-card__footer">
          <span className="hs-card__stats">
            {fmtNum(post.likes) && (
              <span>
                <i className="feather-heart" /> {fmtNum(post.likes)}
              </span>
            )}
            {fmtNum(post.comments) && (
              <span>
                <i className="feather-message-circle" /> {fmtNum(post.comments)}
              </span>
            )}
          </span>
          {post.permalink && (
            <Tooltip title="Open original post">
              <a
                href={post.permalink}
                target="_blank"
                rel="noopener noreferrer"
                className="hs-card__open"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="feather-external-link" />
              </a>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ import modal ----------------------------- */

function ImportModal({
  open,
  siteId,
  job,
  posts,
  categories,
  onClose,
  onDone,
}) {
  const [categoryList, setCategoryList] = useState([]);
  const [duration, setDuration] = useState(10);
  const [scheduleType, setScheduleType] = useState('all');
  const [range, setRange] = useState(null);
  const [preferVideo, setPreferVideo] = useState(true);
  const [useCaption, setUseCaption] = useState(true);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setResult(null);
      setTouched(false);
    }
  }, [open]);

  const videoCount = posts.filter((p) => p.isVideo && p.hasVideoFile).length;
  const missingVideoFile = posts.filter(
    (p) => p.isVideo && !p.hasVideoFile
  ).length;
  const playlistError = touched && !categoryList.length;
  const dateError =
    touched && scheduleType === 'date' && !(range && range[0] && range[1]);

  const submit = async () => {
    setTouched(true);
    if (
      !categoryList.length ||
      (scheduleType === 'date' && !(range && range[0] && range[1]))
    )
      return;
    setSaving(true);
    try {
      const res = await importScrapedPosts({
        siteId,
        id: job._id,
        postIds: posts.map((p) => p.postId),
        categoryList,
        duration,
        type: scheduleType,
        start: scheduleType === 'date' ? range[0].toISOString() : undefined,
        end: scheduleType === 'date' ? range[1].toISOString() : undefined,
        preferVideo,
        useCaption,
      });
      setResult(res.data);
      if (res.data.imported) toastr.success('', res.message);
      onDone(res.data);
    } catch (e) {
      toastr.error('', e.message);
    } finally {
      setSaving(false);
    }
  };

  const preview = posts.slice(0, 7);

  return (
    <Modal
      open={open}
      title={
        result
          ? 'Import result'
          : `Import ${posts.length} post${posts.length > 1 ? 's' : ''} to Media`
      }
      className="adsign-modal hs-import-modal"
      closeIcon={<i className="feather-x" />}
      onCancel={saving ? undefined : onClose}
      maskClosable={!saving}
      width={560}
      destroyOnClose
      footer={
        result
          ? [
              <Button
                key="done"
                type="primary"
                className="button-submit"
                onClick={onClose}
              >
                Done
              </Button>,
            ]
          : [
              <Button
                key="cancel"
                className="button-cancel"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </Button>,
              <Button
                key="ok"
                type="primary"
                className="button-submit"
                loading={saving}
                onClick={submit}
              >
                {saving ? 'Importing…' : `Import ${posts.length}`}
              </Button>,
            ]
      }
    >
      {result ? (
        <div className="hs-result">
          <div className="hs-result__stats">
            <div className="hs-result__stat is-success">
              <b>{result.imported}</b>
              <span>Imported</span>
            </div>
            <div className="hs-result__stat">
              <b>{result.skipped.length}</b>
              <span>Skipped</span>
            </div>
            <div
              className={`hs-result__stat${
                result.failed.length ? ' is-danger' : ''
              }`}
            >
              <b>{result.failed.length}</b>
              <span>Failed</span>
            </div>
          </div>
          {result.imported > 0 && (
            <p className="hs-muted">
              {result.mediaCreated} media item
              {result.mediaCreated > 1 ? 's' : ''} created. If this site
              requires approval, they're waiting under <b>Unpublished Items</b>.
            </p>
          )}
          {[...result.skipped, ...result.failed].length > 0 && (
            <ul className="hs-result__list">
              {[...result.skipped, ...result.failed].map((r) => {
                const post = posts.find((p) => p.postId === r.postId);
                return (
                  <li key={r.postId}>
                    <span>@{post?.author || r.postId}</span>
                    <span className="hs-muted">{r.reason}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : (
        <Spin spinning={saving} tip="Downloading & uploading media…">
          <div className="hs-import">
            <div className="hs-import__strip">
              {preview.map((p) => (
                <div key={p.postId} className="hs-import__thumb">
                  <Thumb
                    siteId={siteId}
                    jobId={job._id}
                    postId={p.postId}
                    alt=""
                  />
                  {p.isVideo && <i className="feather-play" />}
                </div>
              ))}
              {posts.length > preview.length && (
                <div className="hs-import__more">
                  +{posts.length - preview.length}
                </div>
              )}
            </div>

            <div className="form">
              <label className="form-label">
                Playlist <span className="is-required">*</span>
              </label>
              <Select
                mode="multiple"
                placeholder="Select playlist"
                value={categoryList}
                onChange={setCategoryList}
                status={playlistError ? 'error' : undefined}
                optionFilterProp="label"
                options={(categories || []).map((c) => ({
                  value: c._id,
                  label: c.name,
                }))}
                style={{ width: '100%' }}
              />
              {playlistError && (
                <p className="hs-error">Select at least one playlist</p>
              )}
            </div>

            <div className="hs-import__row">
              <div className="form">
                <label className="form-label">Image duration</label>
                <InputNumber
                  min={1}
                  max={3600}
                  value={duration}
                  onChange={(v) => setDuration(v || 10)}
                  addonAfter="sec"
                  style={{ width: '100%' }}
                />
                <p className="hs-muted">Videos use their own length.</p>
              </div>
              <div className="form">
                <label className="form-label">Schedule</label>
                <Radio.Group
                  value={scheduleType}
                  onChange={(e) => setScheduleType(e.target.value)}
                >
                  <Radio value="all">Always</Radio>
                  <Radio value="date">Date range</Radio>
                </Radio.Group>
              </div>
            </div>

            {scheduleType === 'date' && (
              <div className="form">
                <label className="form-label">
                  Start Date / End Date <span className="is-required">*</span>
                </label>
                <DatePicker.RangePicker
                  showTime
                  format="DD/MM/YYYY HH:mm"
                  value={range}
                  onChange={setRange}
                  status={dateError ? 'error' : undefined}
                  style={{ width: '100%' }}
                />
                {dateError && (
                  <p className="hs-error">Pick a start and end date</p>
                )}
              </div>
            )}

            <div className="hs-import__toggles">
              <label className="hs-toggle">
                <Switch
                  size="small"
                  checked={preferVideo}
                  onChange={setPreferVideo}
                />
                <span>
                  Import video file when available
                  <small className="hs-muted">
                    {videoCount} video file{videoCount !== 1 ? 's' : ''}{' '}
                    available
                    {missingVideoFile > 0 &&
                      ` · ${missingVideoFile} video post(s) will use the cover image`}
                  </small>
                </span>
              </label>
              <label className="hs-toggle">
                <Switch
                  size="small"
                  checked={useCaption}
                  onChange={setUseCaption}
                />
                <span>Use caption as description</span>
              </label>
            </div>
          </div>
        </Spin>
      )}
    </Modal>
  );
}

/* --------------------------------- main ---------------------------------- */

function HashtagScraper({ open, onClose, siteId, categories, onImported }) {
  const [status, setStatus] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [job, setJob] = useState(null);
  const [jobLoading, setJobLoading] = useState(false);

  // search form
  const [platform, setPlatform] = useState('instagram');
  const [hashtags, setHashtags] = useState([]);
  const [limit, setLimit] = useState(30);
  const [includeVideos, setIncludeVideos] = useState(false);
  const [starting, setStarting] = useState(false);

  // results
  const [selected, setSelected] = useState(new Set());
  const [typeFilter, setTypeFilter] = useState('all');
  const [hideImported, setHideImported] = useState(true);
  const [query, setQuery] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [importPosts, setImportPosts] = useState([]);
  const [now, setNow] = useState(Date.now());

  const pollRef = useRef(null);

  const loadJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const res = await listScrapes(siteId);
      setJobs(res.data || []);
    } catch (e) {
      toastr.error('', e.message);
    } finally {
      setJobsLoading(false);
    }
  }, [siteId]);

  const loadJob = useCallback(
    async (id, { silent } = {}) => {
      if (!silent) setJobLoading(true);
      try {
        const res = await getScrape({ siteId, id });
        setJob(res.data);
        setJobs((prev) =>
          prev.map((j) =>
            j._id === id ? { ...j, ...res.data, items: undefined } : j
          )
        );
        return res.data;
      } catch (e) {
        if (!silent) toastr.error('', e.message);
        return null;
      } finally {
        if (!silent) setJobLoading(false);
      }
    },
    [siteId]
  );

  // open / close
  useEffect(() => {
    if (!open) return undefined;
    getApifyStatus()
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ configured: false, platforms: [] }));
    loadJobs();
    return () => {
      clearTimeout(pollRef.current);
    };
  }, [open, loadJobs]);

  useEffect(() => {
    if (!open) {
      clearThumbCache();
      setActiveId(null);
      setJob(null);
      setSelected(new Set());
    }
  }, [open]);

  // select a job
  useEffect(() => {
    clearTimeout(pollRef.current);
    setSelected(new Set());
    setQuery('');
    setTypeFilter('all');
    if (activeId) loadJob(activeId);
    else setJob(null);
  }, [activeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // poll while running
  useEffect(() => {
    clearTimeout(pollRef.current);
    if (!open || !job || !isRunning(job)) return undefined;
    pollRef.current = setTimeout(
      () => loadJob(job._id, { silent: true }),
      POLL_MS
    );
    return () => clearTimeout(pollRef.current);
  }, [open, job, loadJob]);

  // elapsed timer
  useEffect(() => {
    if (!isRunning(job)) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [job]);

  const handleTags = (vals) => {
    const max = status?.maxHashtags || 5;
    const cleaned = Array.from(new Set(vals.map(cleanTag).filter(Boolean)));
    const invalid = cleaned.find((t) => !HASHTAG_RE.test(t));
    if (invalid) toastr.warning('', `#${invalid} is not a valid hashtag`);
    const valid = cleaned.filter((t) => HASHTAG_RE.test(t));
    if (valid.length > max)
      toastr.warning('', `Max ${max} hashtags per search`);
    setHashtags(valid.slice(0, max));
  };

  const handleScrape = async () => {
    if (!hashtags.length) {
      toastr.error('', 'Enter at least one hashtag');
      return;
    }
    setStarting(true);
    try {
      const res = await startScrape({
        siteId,
        platform,
        hashtags,
        limit,
        includeVideos,
      });
      setJobs((prev) => [res.data, ...prev]);
      setActiveId(res.data._id);
    } catch (e) {
      toastr.error('', e.message);
    } finally {
      setStarting(false);
    }
  };

  const handleAbort = async () => {
    try {
      const res = await abortScrape({ siteId, id: job._id });
      setJob((j) => ({ ...j, ...res.data }));
    } catch (e) {
      toastr.error('', e.message);
    }
  };

  const handleRemove = async (id) => {
    try {
      await removeScrape({ siteId, id });
      setJobs((prev) => prev.filter((j) => j._id !== id));
      if (activeId === id) setActiveId(null);
    } catch (e) {
      toastr.error('', e.message);
    }
  };

  const rerun = (j) => {
    setPlatform(j.platform);
    setHashtags(j.hashtags);
    setLimit(j.limit);
    setIncludeVideos(Boolean(j.includeVideos));
    setActiveId(null);
  };

  const items = job?.items || [];
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((p) => {
      if (hideImported && p.imported) return false;
      if (typeFilter === 'image' && p.isVideo) return false;
      if (typeFilter === 'video' && !p.isVideo) return false;
      if (
        q &&
        !`${p.caption} ${p.author} ${p.authorName}`.toLowerCase().includes(q)
      )
        return false;
      return true;
    });
  }, [items, hideImported, typeFilter, query]);

  const selectable = visible.filter((p) => !p.imported);
  const allSelected =
    selectable.length > 0 && selectable.every((p) => selected.has(p.postId));
  const importedCount = items.filter((p) => p.imported).length;
  const selectedPosts = items.filter((p) => selected.has(p.postId));

  const toggle = (postId) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });

  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) selectable.forEach((p) => next.delete(p.postId));
      else selectable.forEach((p) => next.add(p.postId));
      return next;
    });

  const onImportDone = async (res) => {
    if (res.imported) {
      setSelected(new Set());
      onImported && onImported();
    }
    await loadJob(job._id, { silent: true });
  };

  const platforms = status?.platforms?.length
    ? status.platforms
    : Object.keys(PLATFORM_META).map((value) => ({
        value,
        label: PLATFORM_META[value].label,
      }));
  const elapsed = job?.createdAt
    ? Math.max(0, Math.floor((now - new Date(job.createdAt)) / 1000))
    : 0;

  /* --------------------------------- body --------------------------------- */

  const renderBody = () => {
    if (status && !status.configured) {
      return (
        <div className="hs-state">
          <i className="feather-alert-triangle hs-state__icon is-warning" />
          <h3>Apify is not configured</h3>
          <p className="hs-muted">
            Set <code>APIFY_TOKEN</code> in the API environment and restart the
            server.
          </p>
        </div>
      );
    }
    if (!activeId) {
      return (
        <div className="hs-state">
          <i className="feather-hash hs-state__icon" />
          <h3>Find posts by hashtag</h3>
          <p className="hs-muted">
            Enter one or more hashtags above and hit <b>Scrape</b>. Results
            usually arrive within 30–90 seconds. Pick the posts you like, then
            import them straight into your Media playlists.
          </p>
        </div>
      );
    }
    if (jobLoading && !job) {
      return (
        <div className="hs-state">
          <Spin />
        </div>
      );
    }
    if (!job) return null;

    if (isRunning(job)) {
      return (
        <div className="hs-state">
          <div className="hs-pulse">
            <i
              className={PLATFORM_META[job.platform]?.icon || 'feather-hash'}
            />
          </div>
          <h3>
            Scraping {job.hashtags.map((h) => `#${h}`).join(' ')} on{' '}
            {platformLabel(job.platform)}
          </h3>
          <p className="hs-muted">
            {STATUS_PILL[job.status]?.label || job.status} ·{' '}
            {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}{' '}
            elapsed · up to {job.limit} posts
            {job.hashtags.length > 1 ? ' per hashtag' : ''}
          </p>
          {job.statusMessage && (
            <p className="hs-state__msg">{job.statusMessage}</p>
          )}
          <p className="hs-muted">
            You can close this window — the scrape keeps running and shows up in
            Recent searches.
          </p>
          {job.status !== 'ABORTING' && (
            <Button className="button-cancel" onClick={handleAbort}>
              Cancel scrape
            </Button>
          )}
        </div>
      );
    }

    if (!items.length) {
      const failed = ['FAILED', 'TIMED-OUT', 'ABORTED'].includes(job.status);
      return (
        <div className="hs-state">
          <i
            className={`${
              failed ? 'feather-x-circle is-danger' : 'feather-search'
            } hs-state__icon`}
          />
          <h3>
            {failed
              ? `Scrape ${STATUS_PILL[job.status]?.label.toLowerCase()}`
              : 'No posts found'}
          </h3>
          <p className="hs-muted">
            {job.statusMessage ||
              'No public posts with images were returned. Try another hashtag or a different platform.'}
          </p>
          <Button className="button-cancel" onClick={() => rerun(job)}>
            <i className="feather-refresh-cw" /> Try again
          </Button>
        </div>
      );
    }

    return visible.length ? (
      <div className="hs-grid">
        {visible.map((p) => (
          <PostCard
            key={p.postId}
            siteId={siteId}
            jobId={job._id}
            post={p}
            selected={selected.has(p.postId)}
            onToggle={toggle}
          />
        ))}
      </div>
    ) : (
      <div className="hs-state">
        <Empty description="No posts match these filters" />
      </div>
    );
  };

  const showResults = job && !isRunning(job) && items.length > 0;

  return (
    <ConfigProvider theme={theme}>
      <Modal
        open={open}
        onCancel={onClose}
        title={
          <span className="hs-title">
            <i className="feather-hash" /> Import from Hashtag
          </span>
        }
        className="adsign-modal hs-modal"
        closeIcon={<i className="feather-x" />}
        width={1180}
        footer={null}
        destroyOnClose
        centered
      >
        <div className="hs">
          <aside className="hs-side">
            <div className="hs-side__head">
              <span>Recent searches</span>
              <Tooltip title="Refresh">
                <button
                  type="button"
                  className="hs-icon-btn"
                  onClick={loadJobs}
                >
                  <i
                    className={`feather-refresh-cw${
                      jobsLoading ? ' hs-spin' : ''
                    }`}
                  />
                </button>
              </Tooltip>
            </div>
            <button
              type="button"
              className={`hs-side__new${!activeId ? ' is-active' : ''}`}
              onClick={() => setActiveId(null)}
            >
              <i className="feather-plus" /> New search
            </button>
            <div className="hs-side__list">
              {!jobs.length && !jobsLoading && (
                <p className="hs-muted hs-side__empty">No searches yet</p>
              )}
              {jobs.map((j) => {
                const pill = STATUS_PILL[j.status] || {
                  label: j.status,
                  tone: 'muted',
                };
                return (
                  <div
                    key={j._id}
                    role="button"
                    tabIndex={0}
                    className={`hs-job${
                      activeId === j._id ? ' is-active' : ''
                    }`}
                    onClick={() => setActiveId(j._id)}
                    onKeyDown={(e) => e.key === 'Enter' && setActiveId(j._id)}
                  >
                    <div className="hs-job__top">
                      <span className={`hs-dot hs-dot--${j.platform}`} />
                      <span className="hs-job__tags">
                        {j.hashtags.map((h) => `#${h}`).join(' ')}
                      </span>
                      <Popconfirm
                        title="Remove this search?"
                        description="Imported media stays in the library."
                        okText="Remove"
                        onConfirm={(e) => {
                          e && e.stopPropagation();
                          handleRemove(j._id);
                        }}
                        onCancel={(e) => e && e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="hs-icon-btn hs-job__remove"
                          onClick={(e) => e.stopPropagation()}
                          aria-label="Remove search"
                        >
                          <i className="feather-trash-2" />
                        </button>
                      </Popconfirm>
                    </div>
                    <div className="hs-job__bottom">
                      <span className={`hs-pill hs-pill--${pill.tone}`}>
                        {pill.label}
                      </span>
                      {j.itemsFetched && (
                        <span className="hs-muted">{j.itemCount} posts</span>
                      )}
                      <span className="hs-muted hs-job__date">
                        {dayjs(j.createdAt).format('DD MMM, HH:mm')}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </aside>

          <section className="hs-main">
            <div className="hs-search">
              <Segmented
                value={platform}
                onChange={setPlatform}
                options={platforms.map((p) => ({
                  value: p.value,
                  label: (
                    <span className="hs-seg">
                      <i className={PLATFORM_META[p.value]?.icon} />
                      {p.label}
                    </span>
                  ),
                }))}
              />
              <HashtagInput
                value={hashtags}
                onChange={handleTags}
                onSubmit={handleScrape}
                placeholder="Type a hashtag and press Enter, e.g. coffee"
              />
              <Tooltip title="Max posts per hashtag">
                <InputNumber
                  min={1}
                  max={status?.maxResults || 100}
                  value={limit}
                  onChange={(v) => setLimit(v || 1)}
                  addonBefore="Max"
                  className="hs-search__limit"
                />
              </Tooltip>
              {platform === 'tiktok' && (
                <Tooltip title="Also download TikTok video files (extra Apify usage). Otherwise only the cover image is imported.">
                  <label className="hs-toggle hs-toggle--inline">
                    <Switch
                      size="small"
                      checked={includeVideos}
                      onChange={setIncludeVideos}
                    />
                    <span>Video files</span>
                  </label>
                </Tooltip>
              )}
              <Button
                type="primary"
                className="hs-search__btn"
                loading={starting}
                disabled={status && !status.configured}
                onClick={handleScrape}
              >
                {!starting && <i className="feather-search" />} Scrape
              </Button>
            </div>

            {showResults && (
              <div className="hs-summary">
                <span
                  className={`hs-badge hs-badge--${job.platform} hs-badge--static`}
                >
                  <i className={PLATFORM_META[job.platform]?.icon} />
                  {platformLabel(job.platform)}
                </span>
                <b>{job.hashtags.map((h) => `#${h}`).join(' ')}</b>
                <span className="hs-muted">
                  {items.length} posts · scraped{' '}
                  {fmtDate(job.finishedAt || job.createdAt)}
                </span>
                {job.status !== 'SUCCEEDED' && (
                  <span className="hs-pill hs-pill--warning">
                    Partial · {STATUS_PILL[job.status]?.label}
                  </span>
                )}
              </div>
            )}

            {showResults && (
              <div className="hs-toolbar">
                <div className="hs-toolbar__left">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={!allSelected && selected.size > 0}
                    onChange={toggleAll}
                  >
                    Select all
                  </Checkbox>
                  <Segmented
                    size="small"
                    value={typeFilter}
                    onChange={setTypeFilter}
                    options={[
                      { value: 'all', label: 'All' },
                      { value: 'image', label: 'Images' },
                      { value: 'video', label: 'Videos' },
                    ]}
                  />
                  <Checkbox
                    checked={hideImported}
                    onChange={(e) => setHideImported(e.target.checked)}
                  >
                    Hide imported{importedCount ? ` (${importedCount})` : ''}
                  </Checkbox>
                </div>
                <Input
                  allowClear
                  size="small"
                  prefix={<i className="feather-search hs-muted" />}
                  placeholder="Filter caption or author"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="hs-toolbar__search"
                />
              </div>
            )}

            <div className="hs-body">{renderBody()}</div>

            {selected.size > 0 && (
              <div className="hs-actionbar">
                <span>
                  <b>{selected.size}</b> post{selected.size > 1 ? 's' : ''}{' '}
                  selected
                </span>
                <div className="hs-actionbar__btns">
                  <Button type="text" onClick={() => setSelected(new Set())}>
                    Clear
                  </Button>
                  <Button
                    type="primary"
                    className="button-submit"
                    onClick={() => {
                      setImportPosts(selectedPosts);
                      setImportOpen(true);
                    }}
                  >
                    <i className="feather-download-cloud" /> Import to Media
                  </Button>
                </div>
              </div>
            )}
          </section>
        </div>

        {job && (
          <ImportModal
            open={importOpen}
            siteId={siteId}
            job={job}
            posts={importPosts}
            categories={categories}
            onClose={() => setImportOpen(false)}
            onDone={onImportDone}
          />
        )}
      </Modal>
    </ConfigProvider>
  );
}

export default HashtagScraper;
